import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, HeartHandshake } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TIER_META = {
  platinum: { label: "Platinum", color: "#E5E4E2", glow: "rgba(229,228,226,0.25)" },
  gold:     { label: "Gold",     color: "#FFD700", glow: "rgba(255,215,0,0.25)" },
  silver:   { label: "Silver",   color: "#C0C0C0", glow: "rgba(192,192,192,0.2)" },
  supporter:{ label: "Supporter", color: "#00d4ff", glow: "rgba(0,212,255,0.18)" },
};
const TIER_ORDER = ["platinum", "gold", "silver", "supporter"];

export default function SponsorsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site/sponsors")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = TIER_ORDER.map((t) => ({
    tier: t,
    items: items.filter((s) => (s.tier || "supporter") === t),
  })).filter((g) => g.items.length);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="sponsors-page">
      {user ? <Navbar /> : null}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-10 py-10">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ sponsors</div>
        <h1 className="font-heading mt-2 flex items-center gap-3"><HeartHandshake className="h-8 w-8 brand-text" strokeWidth={1.25} /> Sponsors &amp; Supporters</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Split the ____ stays free because of these wonderful folks. Thank you — you keep the lights on.
        </p>

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center" data-testid="sponsors-empty">
            <p className="text-sm text-muted-foreground">We don&apos;t have any sponsors yet — be the first to help us grow.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-12">
            {grouped.map(({ tier, items: tierItems }) => {
              const meta = TIER_META[tier] || TIER_META.supporter;
              return (
                <section key={tier} data-testid={`sponsors-tier-${tier}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="h-2 w-2 rounded-full" style={{ background: meta.color, boxShadow: `0 0 12px ${meta.glow}` }} />
                    <div className="text-xs font-mono uppercase tracking-[0.3em]" style={{ color: meta.color }}>{meta.label}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tierItems.map((s) => (
                      <motion.a
                        key={s.id}
                        href={s.url || "#"}
                        target={s.url ? "_blank" : undefined}
                        rel={s.url ? "noopener noreferrer" : undefined}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="group rounded-xl border border-border bg-card/40 p-5 hover:border-[color:var(--brand)] transition relative overflow-hidden"
                        style={{ borderColor: undefined }}
                        data-testid={`sponsor-${s.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="h-12 w-12 rounded-lg bg-foreground/[0.04] flex items-center justify-center text-2xl shrink-0">{s.logo_emoji || "✨"}</span>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate flex items-center gap-1.5">
                              {s.name}
                              {s.url && <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:brand-text transition shrink-0" strokeWidth={1.5} />}
                            </div>
                            {s.note && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3">{s.note}</p>}
                          </div>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 text-center" data-testid="sponsors-cta">
          <p className="text-sm text-muted-foreground">Want your name here?</p>
          <a href="/feedback" className="brand-text text-sm hover:underline">Reach out via Feedback &rarr;</a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
